import { useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Container, Row } from "react-bootstrap";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import SignatureCanvas from "react-signature-canvas";
import Flight, { FlightWbLoad, FlightWbRevisionChanges } from "../../models/flight";
import { Passenger, getPassengerFullName } from "../../models/passenger";
import { getFlightStopPoints, getPassengerStops } from "../../models/flightLegs";
import { AviTracerApi } from "../../avitracerApi";
import { auth } from "../../firebase";
import { useAppSelector } from "../../store/hooks";
import { selectedOrganization } from "../../store/organizationsSlice";
import { useWbSheet } from "../../components/wb/useWbSheet";
import WbSheetView, { WbStepper } from "../../components/wb/WbSheetView";
import { createWbPdfBlob, fmtMass } from "../../utils/wbLoadsheet";
import { FuelConverter } from "../../utils/FuelUtils";
import { errorText } from "../organization/flights/FlightWbPage";
import "../../css/wb-loadsheet.css";

/**
 * Last-minute W&B revision from the pilot app: actual fuel, no-shows (independent of the boarding
 * pass check-in), baggage and seating → a regenerated loadsheet appended to the briefing pack as a
 * signed "Revised W&B" addendum. PIC or an org admin may submit; only the PIC signs.
 * ?sign=n opens straight on the signature pad for a revision still awaiting the PIC's signature.
 */

type Step = "fuel" | "passengers" | "load" | "sign";

//on a desktop the page keeps the Edit Flight W&B panel's width instead of stretching across the screen
const PAGE_MAX_WIDTH = 1000;

function FlyWbRevisionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loadedFlight = useLoaderData() as Flight;
  const selectedOrg = useAppSelector(selectedOrganization);

  const [flight, setFlight] = useState<Flight>(loadedFlight);
  const isPic = flight.pic.uid !== undefined && flight.pic.uid === auth.currentUser?.uid;
  const isAdmin = selectedOrg?.role === "admin";
  const signOnly = searchParams.get("sign");
  //a revision whatever the briefing status: with no pack yet it is added when the pack is built
  const packReady = flight.briefing?.status === "ready";

  //the revision starts from the W&B currently in force: the latest revision, else the planned one
  const latest = flight.wbRevisions?.[flight.wbRevisions.length - 1];
  const baseWb = latest?.wb ?? flight.wb;
  const initialNoShows = useMemo(() => Object.keys(flight.noShows ?? {}), [flight.noShows]);
  const [noShowIds, setNoShowIds] = useState<string[]>(initialNoShows);

  const isLatArmInWbPointUp = selectedOrg?.isLatArmInWbPointUp ?? false;
  const sheet = useWbSheet({ flight, initialWb: baseWb, isLatArmInWbPointUp, noShowIds, revision: true });
  //the untouched state, to tell what changed
  const base = useWbSheet({ flight, initialWb: baseWb, isLatArmInWbPointUp, noShowIds: initialNoShows, revision: true });

  const [step, setStep] = useState<Step>(signOnly ? "sign" : "fuel");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [pendingSign, setPendingSign] = useState<number | undefined>(signOnly ? Number(signOnly) : undefined);
  const signatureRef = useRef<SignatureCanvas>(null);

  const aircraft = flight.aircraft;
  const tanks = aircraft.wb!.stations.filter((s) => s.type === "fuel");
  const legLabel = (i: number) => (sheet.legs.length > 1 ? `Leg ${i + 1} · ${sheet.legs[i].from.name} → ${sheet.legs[i].to.name}` : "Departure");

  // ── no-shows: where each passenger is expected ──
  const stopPoints = useMemo(() => getFlightStopPoints(flight), [flight]);
  const boardingGroups = useMemo(() => {
    const groups: { index: number; passengers: Passenger[] }[] = [];
    flight.passengers.forEach((p) => {
      const from = getPassengerStops(flight, p.id).fromIndex;
      const g = groups.find((x) => x.index === from);
      if (g) g.passengers.push(p);
      else groups.push({ index: from, passengers: [p] });
    });
    return groups.sort((a, b) => a.index - b.index);
  }, [flight]);

  const passengerRow = (p: Passenger) => {
    const noShow = p.id !== undefined && noShowIds.includes(p.id);
    const wasNoShow = p.id !== undefined && initialNoShows.includes(p.id);
    const stops = getPassengerStops(flight, p.id);
    return (
      <div key={p.id} className={"wb-paxrow d-flex justify-content-between align-items-center border-bottom py-3 px-2 gap-3" + (noShow !== wasNoShow ? " changed" : "")}>
        <div>
          <div className={noShow ? "text-decoration-line-through text-muted" : "fw-semibold"}>{getPassengerFullName(p)}</div>
          <div className="small text-muted">
            {stopPoints.length > 2 && (
              <>
                {stopPoints[stops.fromIndex]?.name} → {stopPoints[stops.toIndex]?.name} ·{" "}
              </>
            )}
            {p.weight} {aircraft.massUnit}
            {flight.checkIns?.[p.id!] !== undefined && (
              <Badge bg="success" className="ms-2">
                Boarded
              </Badge>
            )}
          </div>
          {noShow !== wasNoShow ? (
            <div className="wb-paxrow-note">{noShow ? "Marked no show in this revision" : `Back on board (no show in ${baseName})`}</div>
          ) : (
            wasNoShow && <div className="small text-muted">No show since {baseName}</div>
          )}
        </div>
        <div className="wb-seg flex-shrink-0" role="group" aria-label={"Show status of " + getPassengerFullName(p)}>
          <button type="button" className={!noShow ? "on" : ""} disabled={!p.id} onClick={() => setNoShowIds((prev) => prev.filter((x) => x !== p.id))}>
            On board
          </button>
          <button type="button" id={"noshow-" + p.id} className={noShow ? "on" : ""} disabled={!p.id}
            onClick={() => setNoShowIds((prev) => (prev.includes(p.id!) ? prev : [...prev, p.id!]))}>
            No show
          </button>
        </div>
      </div>
    );
  };

  // ── what changed ──
  const changes: FlightWbRevisionChanges = useMemo(() => {
    const fuel = sheet.sheetLegs
      .map((l, i) => ({
        legIndex: i,
        before: Math.round(base.sheetLegs[i]?.input.fuelOnBoard.reduce((acc, f) => acc + f.mass, 0) ?? 0),
        after: Math.round(l.input.fuelOnBoard.reduce((acc, f) => acc + f.mass, 0)),
      }))
      .filter((f) => f.before !== f.after);
    const baggage = sheet.sheetLegs.flatMap((l, i) =>
      aircraft.wb!.stations
        .filter((s) => s.type === "baggage")
        .map((s) => ({
          legIndex: i,
          stationId: s.id,
          before: base.sheetLegs[i]?.input.wbLoads.find((x) => x.station.id === s.id)?.mass ?? 0,
          after: l.input.wbLoads.find((x) => x.station.id === s.id)?.mass ?? 0,
        }))
        .filter((b) => b.before !== b.after)
    );
    const seatKey = (loads: FlightWbLoad[]) =>
      loads
        .filter((x) => x.station.type === "seat")
        .map((x) => `${x.station.id}:${x.personId ?? x.name}:${x.mass}`)
        .sort()
        .join("|");
    const noShows = noShowIds.filter((id) => !initialNoShows.includes(id));
    //a no-show empties seats by itself — "seating" means the cabin was edited beyond that
    const seating = sheet.sheetLegs.some((l, i) => {
      const baseLoads = (base.sheetLegs[i]?.input.wbLoads ?? []).filter((x) => !(x.personId && noShowIds.includes(x.personId)));
      return seatKey(l.input.wbLoads) !== seatKey(baseLoads);
    });
    return {
      ...(fuel.length ? { fuel } : {}),
      ...(noShows.length ? { noShows } : {}),
      ...(baggage.length ? { baggage } : {}),
      ...(seating ? { seating } : {}),
    };
  }, [sheet.sheetLegs, base.sheetLegs, noShowIds, initialNoShows, aircraft]);

  const changeSummary = (c: FlightWbRevisionChanges): string[] => {
    const lines: string[] = [];
    const legName = (i: number) => (sheet.legs.length > 1 ? `Leg ${i + 1} ` : "");
    c.fuel?.forEach((f) => lines.push(`${legName(f.legIndex)}fuel ${fmtMass(f.before)} → ${fmtMass(f.after)} ${aircraft.massUnit}`));
    c.noShows?.forEach((id) => {
      const p = flight.passengers.find((x) => x.id === id);
      lines.push(`No show: ${p ? getPassengerFullName(p) : id}`);
    });
    c.baggage?.forEach((b) => {
      const station = aircraft.wb!.stations.find((s) => s.id === b.stationId);
      lines.push(`${legName(b.legIndex)}${station?.name ?? b.stationId} ${fmtMass(b.before)} → ${fmtMass(b.after)} ${aircraft.massUnit}`);
    });
    if (c.seating) lines.push("Seating changed");
    return lines;
  };
  const hasChanges = Object.keys(changes).length > 0;

  //what submitting creates, and what it is compared with: the W&B in force (latest revision, else the plan)
  const nextRevision = (flight.wbRevisions?.length ?? 0) + 1;
  const baseName = latest ? `revision ${latest.n}` : "planned";
  const packNote = !packReady
    ? "It is added to the briefing pack when the pack is generated."
    : flight.briefingPilotAccept !== undefined
    ? "It is added to the signed briefing pack and needs the PIC's signature."
    : "It is added to the briefing pack and signed with it.";
  const changeCount = hasChanges ? changeSummary(changes).length : 0;

  // ── submit ──
  const submit = async () => {
    if (sheet.hasErrors || !hasChanges) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const n = (flight.wbRevisions?.length ?? 0) + 1;
      const revision = { n, summary: changeSummary(changes), fuelChanged: changes.fuel !== undefined };
      const pdf = await createWbPdfBlob(sheet.sheetLegs.map((l) => ({ ...l.input, revision })));
      await AviTracerApi.uploadWbRevisionPdf(flight.id!, pdf);
      const updated = await AviTracerApi.submitWbRevision(flight.id!, { changes, wb: sheet.buildWb(), noShows: noShowIds });
      setFlight(updated);
      if (isPic && updated.briefingPilotAccept !== undefined) {
        //the pack is already signed: the addendum needs its own signature now
        setPendingSign(n);
        setStep("sign");
      } else {
        //not signed yet (the pack signature covers the addendum), or an admin submitted it — the PIC signs later
        navigate(`/fly/${flight.id}`, { replace: true });
      }
    } catch (e) {
      setError("Submitting the revision failed: " + errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const signRevision = async () => {
    if (pendingSign === undefined) return;
    if (signatureRef.current?.isEmpty()) {
      setError("Please sign in the area above.");
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await AviTracerApi.signWbRevision(flight.id!, pendingSign, signatureRef.current!.getTrimmedCanvas().toDataURL());
      navigate(`/fly/${flight.id}`, { replace: true });
    } catch (e) {
      setError("Signing failed: " + errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isPic && !isAdmin) {
    return (
      <Container className="pt-5">
        <Alert variant="warning">Only the PIC or an operator admin can change the W&amp;B of this flight.</Alert>
        <Button variant="dark" onClick={() => navigate(`/fly/${flight.id}`)}>
          Back
        </Button>
      </Container>
    );
  }

  if (step === "sign") {
    return (
      <>
        <div className="fixed-top">
          <Container>
            <Button disabled={submitting} variant="light" className="bg-white border mt-3" onClick={() => navigate(`/fly/${flight.id}`)}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </Button>
            <div className="bg-white border rounded px-2 py-1 mt-2 small">
              Sign W&amp;B revision {pendingSign}. It is added to your signed briefing pack.
            </div>
            {error && <Alert variant="danger" className="mt-2 py-2">{error}</Alert>}
          </Container>
        </div>
        <div className="vw-100 vh-100 bg-white">
          <SignatureCanvas minWidth={2} maxWidth={5} ref={signatureRef} dotSize={3} canvasProps={{ className: "w-100 h-100" }} />
        </div>
        <div className="fixed-bottom bg-light">
          <div className="d-flex">
            <div className="w-25 d-grid gap-2 m-2">
              <Button disabled={submitting} style={{ height: "60px" }} variant="light" className="fw-bold" onClick={() => signatureRef.current!.clear()}>
                Clear
              </Button>
            </div>
            <div className="w-75 d-grid gap-2 m-2">
              <Button disabled={submitting || !isPic} style={{ height: "60px" }} className="fw-bold" variant="dark" onClick={signRevision}>
                {submitting ? "Saving..." : `Sign Revision ${pendingSign}`}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const steps: Step[] = ["fuel", "passengers", "load"];
  const stepIndex = steps.indexOf(step);
  const stepTitle = { fuel: "Actual fuel", passengers: "Passengers", load: "Load & review", sign: "" }[step];

  return (
    <Container style={{ maxWidth: PAGE_MAX_WIDTH }}>
      <div className="fixed-top bg-white border-bottom">
        <Container className="d-flex align-items-center gap-2 py-2" style={{ maxWidth: PAGE_MAX_WIDTH }}>
          <Button
            variant="light"
            className="bg-white border"
            disabled={submitting}
            onClick={() => (stepIndex > 0 ? setStep(steps[stepIndex - 1]) : navigate(`/fly/${flight.id}`))}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <div className="lh-sm">
            <div className="fw-bold">Last-minute change · {stepTitle}</div>
            <div className="small text-muted">
              {flight.flightId} · step {stepIndex + 1} of {steps.length} · creates revision {nextRevision}
            </div>
          </div>
        </Container>
      </div>

      {/* wb-sheet: the --wb-* tokens the fuel panels and the review sheet are styled with */}
      <Row className="wb-sheet" style={{ paddingTop: "80px" }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {sheet.seedError && <Alert variant="danger">{sheet.seedError}</Alert>}


        {step === "fuel" && (
          <div>
            <p className="text-muted small">
              Fuel actually on board at each departure. The navlog and the filed flight plan keep the planned fuel; the revised loadsheet notes the
              difference.
            </p>
            {sheet.sheetLegs.map((l, i) => (
              <div key={i} className="wb-limits mb-3">
                <div className="wb-limits-head">{legLabel(i)}</div>
                {tanks.map((tank) => {
                  const f = l.input.fuelOnBoard.find((x) => x.station.id === tank.id);
                  const planned = base.sheetLegs[i]?.input.fuelOnBoard.find((x) => x.station.id === tank.id);
                  const delta = Math.round(f?.fuel ?? 0) - Math.round(planned?.fuel ?? 0);
                  const overTank = tank.maxMass > 0 && (f?.mass ?? 0) > Math.ceil(tank.maxMass); //as calculateStationLimitTexts
                  return (
                    <div key={tank.id} className={"wb-lim-row" + (overTank ? " bad" : delta !== 0 ? " changed" : "")}>
                      <span className="wb-lim-key">{tank.name}</span>
                      <span className="wb-lim-val wb-num">
                        <WbStepper
                          value={Math.round(f?.fuel ?? 0)}
                          emphasized
                          onChange={(v) => sheet.setLegFuelAt(i, tank.id, v)}
                        />
                      </span>
                      <span className={"wb-lim-note wb-num" + (delta === 0 && !overTank ? " same" : "")}>
                        {overTank
                          ? `${aircraft.fuelUnit} · over the tank's ${Math.floor(FuelConverter.toFuelUnit(aircraft, tank.maxMass))}`
                          : delta === 0
                          ? `${aircraft.fuelUnit} · same as ${baseName}`
                          : `${aircraft.fuelUnit} · ${baseName} ${Math.round(planned?.fuel ?? 0)} · ${delta > 0 ? "+" : ""}${delta}`}
                      </span>
                      <span className="wb-lim-tick" />
                    </div>
                  );
                })}
                {i > 0 &&
                  (l.fuelCarried ? (
                    <div className="wb-lim-row">
                      <span className="wb-lim-key small text-muted">Carried from the previous leg's landing fuel · follows the legs before it</span>
                    </div>
                  ) : (
                    //a value of its own = refuelled at the stop: it no longer follows the legs before it
                    <div className="wb-lim-row">
                      <span className="wb-lim-key small text-muted">
                        Uplift at {sheet.legs[i].from.name} · landed with{" "}
                        {sheet.sheetLegs[i - 1].calc.landing.landingFuelOnBoard.reduce((acc, x) => acc + x.fuel, 0)} {aircraft.fuelUnit} ·{" "}
                        <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => sheet.resetLegFuelAt(i)}>
                          use carried-over fuel
                        </button>
                      </span>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        )}

        {step === "passengers" && (
          <div>
            <p className="text-muted small">
              Mark passengers who did not show up. This is separate from the boarding pass scan — use it whether or not their pass was checked.
            </p>
            {flight.passengers.length === 0 && <div className="text-muted">No passengers on this flight.</div>}
            {stopPoints.length > 2
              ? //with intermediate stops: grouped by where each passenger is expected to board
                boardingGroups.map((g) => (
                  <div key={g.index} className="mb-4">
                    <div className="small fw-bold text-uppercase text-muted border-bottom pb-2" style={{ letterSpacing: "0.06em" }}>
                      Boarding at {stopPoints[g.index].name} · {g.index === 0 ? "departure" : `stop ${g.index}`}
                    </div>
                    {g.passengers.map(passengerRow)}
                  </div>
                ))
              : flight.passengers.map(passengerRow)}
          </div>
        )}

        {step === "load" && !sheet.seedError && (
          <div>
            {hasChanges ? (
              <div className="wb-status mb-3" style={{ background: "#fdf6e3", borderColor: "#e8c979" }}>
                <span className="wb-status-big" style={{ color: "#8a5a00" }}>
                  Revision {(flight.wbRevisions?.length ?? 0) + 1}
                </span>
                <ul>
                  {changeSummary(changes).map((line, i) => (
                    <li key={i} style={{ color: "#333c46" }}>
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <Alert variant="secondary" className="py-2">
                Nothing changed yet — adjust baggage or seats below, or go back to fuel and passengers.
              </Alert>
            )}
            <WbSheetView sheet={sheet} canEdit={true} fuelEditableFromLeg={Number.MAX_SAFE_INTEGER} />
          </div>
        )}
      </Row>

      {/* room for the bottom bar, taller while it shows the revision notice */}
      <div style={{ height: hasChanges ? "240px" : "120px" }} />
      <div className="fixed-bottom bg-light wb-sheet">
        <div className="d-grid gap-2 p-3 mx-auto" style={{ maxWidth: PAGE_MAX_WIDTH }}>
          {hasChanges && (
            <div className="wb-revnote">
              <div>
                <b>Submitting creates W&amp;B revision {nextRevision}</b>, compared with the {latest ? "current " : ""}
                {baseName} W&amp;B. {packNote}
              </div>
              <div className="wb-revnote-count on">
                {changeCount} change{changeCount > 1 ? "s" : ""}
              </div>
            </div>
          )}
          {step !== "load" ? (
            <Button style={{ height: "60px" }} className="fw-bold" variant="dark" onClick={() => setStep(steps[stepIndex + 1])}>
              Next
            </Button>
          ) : (
            <Button
              style={{ height: "60px" }}
              className="fw-bold"
              variant="dark"
              disabled={submitting || sheet.hasErrors || !hasChanges || sheet.seedError !== undefined}
              onClick={submit}
            >
              {submitting
                ? "Submitting..."
                : sheet.hasErrors
                ? "Out of limits — adjust the load"
                : isPic && flight.briefingPilotAccept !== undefined
                ? "Submit & sign revision"
                : "Submit revision"}
            </Button>
          )}
        </div>
      </div>
    </Container>
  );
}

export default FlyWbRevisionPage;
